import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Courses", to: "/courses" },
  { label: "Contact", to: "/courses#contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goContact = () => {
    setOpen(false);
    navigate("/courses#contact");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-md border-b border-slate-200 ambient-shadow"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <nav className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="logo-link"
        >
          <img
            src="/logo.png"
            alt="Second Chance CPR logo"
            className="h-12 w-12 md:h-14 md:w-14 object-contain"
          />
          <span className="font-editorial text-lg md:text-2xl font-bold text-navy leading-none tracking-tight">
            Second Chance CPR
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className="font-body text-[15px] font-medium text-slate-600 hover:text-teal transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-teal after:transition-all after:duration-300 hover:after:w-full"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {item.label}
            </Link>
          ))}
          <button
            onClick={goContact}
            className="rounded-full bg-red-accent px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#be123c] hover:-translate-y-0.5 ambient-shadow focus:outline-none focus:ring-2 focus:ring-red-accent focus:ring-offset-2"
            data-testid="nav-book-class-btn"
          >
            Book a Class
          </button>
        </div>

        <button
          className="md:hidden grid place-items-center h-11 w-11 rounded-xl border border-slate-200 bg-white/70 text-navy"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-white border-b border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="font-body text-lg font-medium text-navy"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              ))}
              <button
                onClick={goContact}
                className="rounded-full bg-red-accent px-6 py-3.5 text-base font-semibold text-white text-center"
                data-testid="mobile-book-class-btn"
              >
                Book a Class
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
